import React, { useState } from "react";
import { Slider } from "@/components/ui/slider";
import {
  businessLoanCost, formatGBP, formatGBPCompact, formatGBP2,
  amountToPos, posToAmount, SLIDER_STEPS, DEFAULT_INDICATIVE_APR,
} from "@/lib/api";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, Wallet, Truck, Building2, FileText, Rocket, Package,
} from "lucide-react";
import { SectionBadge } from "./Comparison";

const PRODUCTS = [
  {
    id: "working-capital",
    icon: <Wallet className="h-5 w-5" strokeWidth={2.2} />,
    name: "Working Capital",
    from: "from 8.9% APR",
    body: "Cash-flow, VAT bills, stock, payroll. Typically £5k–£1M over 1–60 months.",
  },
  {
    id: "asset-finance",
    icon: <Truck className="h-5 w-5" strokeWidth={2.2} />,
    name: "Asset Finance",
    from: "from 6.9% APR",
    body: "Equipment, vehicles, plant, machinery. Refinance existing assets to release cash.",
  },
  {
    id: "commercial-mortgage",
    icon: <Building2 className="h-5 w-5" strokeWidth={2.2} />,
    name: "Commercial Mortgage",
    from: "from 5.9% APR",
    body: "Buy your premises, portfolio expansion, refinance. Up to £500M over 25 years.",
  },
  {
    id: "invoice-finance",
    icon: <FileText className="h-5 w-5" strokeWidth={2.2} />,
    name: "Invoice Finance",
    from: "from 3.5% fee",
    body: "Release up to 90% of unpaid invoices in 24 hours. Selective or whole-book.",
  },
  {
    id: "growth-capital",
    icon: <Rocket className="h-5 w-5" strokeWidth={2.2} />,
    name: "Growth Capital",
    from: "from 9.9% APR",
    body: "Acquisitions, MBOs, R&D, market expansion. Structured debt & mezz up to £500M.",
  },
  {
    id: "bridging",
    icon: <Package className="h-5 w-5" strokeWidth={2.2} />,
    name: "Bridging Finance",
    from: "from 0.55%/mo",
    body: "Property, auction, refurb, chain-break. Fast turnaround up to 24 months.",
  },
];

export default function Pricing() {
  const [amount, setAmount] = useState(250_000);
  const [term, setTerm] = useState(36);
  const nav = useNavigate();
  const { monthly, total } = businessLoanCost(amount, term);

  return (
    <section id="pricing" className="relative bg-white py-24 md:py-32">
      <div className="max-w-[1280px] mx-auto px-5 md:px-8">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <SectionBadge>Indicative pricing</SectionBadge>
          <h2 className="font-display font-extrabold text-[40px] md:text-[56px] leading-[1.02] tracking-[-0.035em] text-navy mt-5">
            See the <span className="text-emerald">indicative cost</span><br />
            before you apply.
          </h2>
          <p className="mt-6 text-[16px] text-navy/70 font-medium max-w-xl mx-auto leading-[1.6]">
            Final rates are bespoke and depend on turnover, sector, security and trading history. This calculator gives you a working indicative figure across our funding panel.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-6 mb-16">
          <div className="lg:col-span-7 bg-paper-warm rounded-[24px] border border-line p-5 md:p-10">
            <div className="flex items-start justify-between mb-4">
              <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] font-bold text-navy/55">Live indicative</div>
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-soft text-emerald-3 text-[10.5px] font-bold uppercase tracking-widest">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald pulse-dot text-emerald" />
                Updates Live
              </div>
            </div>

            <div className="font-display font-extrabold text-[60px] sm:text-[84px] md:text-[112px] leading-[0.9] tracking-[-0.05em] text-navy mt-2 mb-2">
              {formatGBPCompact(amount)}
            </div>
            <div className="text-[14px] md:text-[15px] text-navy/60 font-semibold mb-7 md:mb-10">
              over <span className="font-extrabold text-navy">{term} {term === 1 ? "month" : "months"}</span> · indicative APR <span className="font-extrabold text-emerald-2">{(DEFAULT_INDICATIVE_APR * 100).toFixed(1)}%</span>
            </div>

            <div className="space-y-6">
              <div>
                <div className="flex justify-between mb-2">
                  <span className="font-mono text-[11px] uppercase tracking-[0.12em] font-bold text-navy/55">Amount</span>
                  <span className="font-mono text-[11px] text-navy/40">£5k · £500M</span>
                </div>
                <Slider
                  data-testid="pricing-amount-slider"
                  value={[amountToPos(amount)]} min={0} max={SLIDER_STEPS} step={1}
                  onValueChange={(v) => setAmount(posToAmount(v[0]))}
                  className="[&>span]:bg-navy/15 [&_span[data-orientation=horizontal]>span]:bg-emerald"
                />
              </div>
              <div>
                <div className="flex justify-between mb-2">
                  <span className="font-mono text-[11px] uppercase tracking-[0.12em] font-bold text-navy/55">Term</span>
                  <span className="font-mono text-[11px] text-navy/40">1 · 120 months</span>
                </div>
                <Slider
                  data-testid="pricing-term-slider"
                  value={[term]} min={1} max={120} step={1}
                  onValueChange={(v) => setTerm(v[0])}
                  className="[&>span]:bg-navy/15 [&_span[data-orientation=horizontal]>span]:bg-emerald"
                />
              </div>
            </div>

            <div className="mt-7 pt-5 md:mt-10 md:pt-7 border-t border-line grid grid-cols-3 gap-3 md:gap-6">
              <Stat label="Monthly" value={formatGBP2(monthly)} />
              <Stat label="APR" fullLabel="Indicative APR" value={`${(DEFAULT_INDICATIVE_APR * 100).toFixed(1)}%`} />
              <Stat label="Total" value={formatGBP(total)} highlight />
            </div>

            <button
              onClick={() => nav(`/apply?amount=${amount}&term=${term}`)}
              data-testid="pricing-apply-btn"
              className="btn-shine mt-7 md:mt-8 group inline-flex w-full md:w-auto items-center justify-center gap-2 h-14 px-8 rounded-xl bg-navy text-white hover:bg-navy-2 font-extrabold text-[15px] transition-colors"
            >
              Apply for {formatGBPCompact(amount)}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" strokeWidth={2.4} />
            </button>
          </div>

          <div className="lg:col-span-5 grid grid-cols-1 gap-4">
            <Rule title="Whole-of-market" body="A panel of 60+ UK lenders covering every mainstream and specialist product." />
            <Rule title="Fees disclosed up-front" body="Any arrangement fees, broker fees or exit fees are shown before you sign — never after." />
            <Rule title="Bespoke rate to the deal" body="Final APR depends on turnover, security, trading history and sector. Rates from 5.9%." />
            <Rule title="Directors protected" body="We prioritise structures that minimise or remove personal guarantees where possible." />
          </div>
        </div>

        {/* Product tier grid */}
        <div className="text-center mb-10">
          <SectionBadge>Funding products</SectionBadge>
          <h3 className="font-display font-extrabold text-[32px] md:text-[44px] leading-[1.02] tracking-[-0.03em] text-navy mt-5">
            One panel. <span className="text-emerald">Every product.</span>
          </h3>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {PRODUCTS.map((p) => (
            <div
              key={p.id}
              data-testid={`product-tile-${p.id}`}
              className="rounded-[20px] bg-paper-warm border border-line p-6 md:p-7 hover:border-emerald/40 hover:-translate-y-1 transition-all duration-300"
            >
              <div className="flex items-start justify-between mb-5">
                <div className="h-11 w-11 rounded-xl bg-emerald-soft text-emerald-3 flex items-center justify-center">
                  {p.icon}
                </div>
                <div className="font-mono text-[10.5px] uppercase tracking-[0.14em] font-extrabold text-emerald-3">{p.from}</div>
              </div>
              <div className="font-display font-extrabold text-[20px] tracking-[-0.02em] text-navy mb-2">{p.name}</div>
              <p className="text-[13.5px] text-navy/65 leading-[1.6] font-medium">{p.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Stat({ label, fullLabel, value, highlight }) {
  return (
    <div className="min-w-0">
      <div className="font-mono text-[10px] sm:text-[10.5px] uppercase tracking-[0.1em] sm:tracking-[0.12em] font-bold text-navy/55 mb-1.5 sm:mb-2 truncate">
        <span className="sm:hidden">{label}</span>
        <span className="hidden sm:inline">{fullLabel || label}</span>
      </div>
      <div className={`font-display font-extrabold text-[18px] sm:text-[22px] md:text-[26px] leading-none tracking-[-0.03em] break-words ${highlight ? "text-emerald-2" : "text-navy"}`}
        style={{ overflowWrap: "anywhere" }}
      >
        {value}
      </div>
    </div>
  );
}
function Rule({ title, body }) {
  return (
    <div className="rounded-[20px] p-5 md:p-6 bg-white border border-line hover:border-emerald/40 transition-colors">
      <div className="font-display font-extrabold text-[16.5px] md:text-[18px] tracking-[-0.02em] text-navy mb-1.5">{title}</div>
      <div className="text-[13.5px] text-navy/65 leading-[1.55] font-medium">{body}</div>
    </div>
  );
}
